/**
 * Calendar subscription feeds (ICS).
 *
 * URLs are built server-side and returned whole: api/client.ts resolves
 * VITE_API_URL to '' (same-origin) in production, so a URL assembled here
 * would be relative — and a relative URL is useless to Google, which fetches
 * the feed from its own servers.
 */

import { api } from './client';

export interface CalendarFeedLink {
  url: string;
}

export interface CalendarOrgFeed {
  orgSlug: string;
  orgName: string;
  /** False when the org's tier or its own switch excludes calendar feeds. */
  eligible: boolean;
  /** Null for ineligible orgs — they are still listed so the UI can say why. */
  url: string | null;
}

export interface CalendarSubscriptions {
  personal: CalendarFeedLink;
  orgs: CalendarOrgFeed[];
}

/** Reads the user's feed URLs, minting a token on first call. */
export async function getCalendarSubscriptions(): Promise<CalendarSubscriptions> {
  const res = await api.get<CalendarSubscriptions>('/calendar/subscriptions');
  return res.data;
}

/** Issues a fresh token; every previously shared URL stops working. */
export async function rotateCalendarToken(): Promise<CalendarSubscriptions> {
  const res = await api.post<CalendarSubscriptions>('/calendar/subscriptions/rotate');
  return res.data;
}

// ---------------------------------------------------------------------------
// One-click subscribe helpers (pure — unit tested without a network)
// ---------------------------------------------------------------------------

/** Opens Google Calendar's "add by URL" dialog with the feed prefilled. */
export function googleAddUrl(feedUrl: string): string {
  return `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(feedUrl)}`;
}

/** webcal:// hands the URL to the OS calendar app (Apple Calendar, Outlook). */
export function webcalUrl(feedUrl: string): string {
  return feedUrl.replace(/^https?:\/\//, 'webcal://');
}
